import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * PLP add-to-cart with persistent label/icon and +/- stepper after first add.
 *
 * @typedef {object} PlpAddToCartRefs
 * @property {HTMLButtonElement} minusButton
 * @property {HTMLButtonElement} plusButton
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} quantityDisplay
 * @extends {Component<PlpAddToCartRefs>}
 */
class PlpAddToCartComponent extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const productCard = this.closest('product-card');
    const productForm = this.closest('product-form-component');

    productCard?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    productForm?.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    this.refs.minusButton?.addEventListener('click', this.#onMinusClick, { signal });
    this.refs.plusButton?.addEventListener('click', this.#onPlusClick, { signal });

    this.#syncFromCart();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** @param {CartUpdateEvent} event */
  #onCartUpdate = (event) => {
    const productForm = this.closest('product-form-component');
    if (!productForm || !event.target || !productForm.contains(/** @type {Node} */ (event.target))) return;
    if (event.detail?.data?.didError) return;

    const cart = /** @type {{ items?: Array<{ variant_id: number, quantity: number }> } | undefined} */ (
      event.detail?.resource
    );
    const variantQty = this.#getVariantQuantityFromCart(cart);
    if (variantQty > 0) {
      this.#enterActiveMode(variantQty);
    }
  };

  /** @param {import('./events').VariantUpdateEvent} _event */
  #onVariantUpdate = (_event) => {
    this.#exitActiveMode();
    this.#syncFromCart();
  };

  #onMinusClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#changeCartQuantity(-1);
  };

  #onPlusClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#changeCartQuantity(1);
  };

  /** @param {number} delta */
  async #changeCartQuantity(delta) {
    const variantId = this.#getVariantId();
    if (!variantId) return;

    const cart = await this.#fetchCart();
    if (!cart?.items) return;

    const lineItem = cart.items.find((item) => String(item.variant_id) === String(variantId));
    if (!lineItem && delta < 0) return;

    const currentQty = lineItem?.quantity ?? 0;
    const nextQty = Math.max(0, currentQty + delta);

    if (nextQty === 0) {
      await this.#updateCartLineQuantity(lineItem, 0);
      this.#exitActiveMode();
      return;
    }

    if (lineItem) {
      await this.#updateCartLineQuantity(lineItem, nextQty);
    } else if (delta > 0) {
      await this.#addVariantToCart(variantId, 1);
    }

    this.#enterActiveMode(nextQty);
  }

  async #syncFromCart() {
    const variantId = this.#getVariantId();
    if (!variantId) {
      this.#exitActiveMode();
      return;
    }

    const cart = await this.#fetchCart();
    const variantQty = this.#getVariantQuantityFromCart(cart);

    if (variantQty > 0) {
      this.#enterActiveMode(variantQty);
    } else {
      this.#exitActiveMode();
    }
  }

  /**
   * @param {number} quantity
   */
  #enterActiveMode(quantity) {
    this.classList.add('plp-add-to-cart--active');

    const { minusButton, plusButton, quantityDisplay } = this.refs;
    minusButton?.removeAttribute('hidden');
    plusButton?.removeAttribute('hidden');

    if (quantityDisplay) {
      quantityDisplay.textContent = String(quantity);
      quantityDisplay.removeAttribute('hidden');
      quantityDisplay.removeAttribute('aria-hidden');
      quantityDisplay.setAttribute('aria-label', String(quantity));
    }

    const addToCartButton = this.querySelector('.plp-add-to-cart__submit');
    if (addToCartButton instanceof HTMLButtonElement && addToCartButton.type === 'submit') {
      addToCartButton.type = 'button';
    }

    addToCartButton?.removeAttribute('data-added');
  }

  #exitActiveMode() {
    this.classList.remove('plp-add-to-cart--active');

    const { minusButton, plusButton, quantityDisplay } = this.refs;
    minusButton?.setAttribute('hidden', '');
    plusButton?.setAttribute('hidden', '');

    if (quantityDisplay) {
      quantityDisplay.textContent = '';
      quantityDisplay.setAttribute('hidden', '');
      quantityDisplay.setAttribute('aria-hidden', 'true');
      quantityDisplay.removeAttribute('aria-label');
    }

    const addToCartButton = this.querySelector('.plp-add-to-cart__submit');
    if (addToCartButton instanceof HTMLButtonElement && addToCartButton.type === 'button') {
      addToCartButton.type = 'submit';
    }

    addToCartButton?.removeAttribute('data-added');
  }

  /** @returns {string | null} */
  #getVariantId() {
    const productForm = this.closest('product-form-component');
    const variantInput = productForm?.querySelector('input[name="id"]');
    return variantInput instanceof HTMLInputElement && variantInput.value ? variantInput.value : null;
  }

  /**
   * @param {{ items?: Array<{ variant_id: number, quantity: number }> } | null | undefined} cart
   * @returns {number}
   */
  #getVariantQuantityFromCart(cart) {
    const variantId = this.#getVariantId();
    if (!variantId || !cart?.items) return 0;

    const lineItem = cart.items.find((item) => String(item.variant_id) === String(variantId));
    return lineItem?.quantity ?? 0;
  }

  /** @returns {Promise<{ items: Array<{ id: number, variant_id: number, quantity: number, key: string }> } | null>} */
  async #fetchCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  /**
   * @param {{ key: string, variant_id: number, quantity: number }} lineItem
   * @param {number} quantity
   */
  async #updateCartLineQuantity(lineItem, quantity) {
    const body = JSON.stringify({ id: lineItem.key, quantity });
    const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
    const cart = await response.json();

    if (cart.errors) {
      console.error(cart.errors);
      return;
    }

    this.#dispatchCartUpdate(cart, quantity);
  }

  /**
   * @param {string} variantId
   * @param {number} quantity
   */
  async #addVariantToCart(variantId, quantity) {
    const body = JSON.stringify({ items: [{ id: Number(variantId), quantity }] });
    const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
    const result = await response.json();

    if (result.status) {
      console.error(result.message);
      return;
    }

    const cart = await this.#fetchCart();
    if (cart) {
      this.#dispatchCartUpdate(cart, quantity);
    }
  }

  /**
   * @param {object} cart
   * @param {number} itemCount
   */
  #dispatchCartUpdate(cart, itemCount) {
    const productForm = this.closest('product-form-component');
    productForm?.dispatchEvent(
      new CartUpdateEvent(cart, productForm.id, {
        source: 'plp-add-to-cart-component',
        itemCount,
        productId: this.dataset.productId,
      })
    );
  }
}

if (!customElements.get('plp-add-to-cart-component')) {
  customElements.define('plp-add-to-cart-component', PlpAddToCartComponent);
}
